/**
 * `/vaikas`: her surface (surfaces.md §5).
 *
 *   code (typed once per device) → the sibling picker on a shared device
 *   → home (her name, her slice) → the work → „Baigti"
 *
 * Her name is in the corner of every screen, so a sibling who sits down
 * notices. Nothing else sits in the header: no counter, no score, no clock.
 * The request button, which never blocks. There is no camera tile: the live
 * session is voice only (D29).
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, BUILD } from "../lib/http";
import { ulid } from "../lib/ulid";
import { type Picker, type Slice, childApi } from "./api";
import { Ctx, type ChildCtx, type Where, makeEmit, useKeys } from "./ctx";
import { type EventQueue, createEventQueue, sendBeacon } from "./queue";
import { readLayout } from "./env";
import { Work } from "./Work";
import { ChildVoiceIndicator } from "../voice/ChildVoice";

type Screen =
  | { at: "boot" }
  | { at: "enroll" }
  | { at: "picker"; picker: Picker; list: boolean; disown?: boolean }
  | { at: "home" }
  | { at: "work" }
  | { at: "ended" };

interface Sitting {
  session: string;
  name: string;
  pick: number | null;
  shared: boolean;
  names: string[];
}

const SITTING_KEY = "ugis.sitting";
const IDLE_MS = 10 * 60_000; // S22

function saveSitting(s: Sitting | null) {
  try {
    if (s) localStorage.setItem(SITTING_KEY, JSON.stringify({ ...s, at: Date.now() }));
    else localStorage.removeItem(SITTING_KEY);
  } catch {
    /* ignore */
  }
}

function loadSitting(): (Sitting & { at: number }) | null {
  try {
    const raw = localStorage.getItem(SITTING_KEY);
    return raw ? (JSON.parse(raw) as Sitting & { at: number }) : null;
  } catch {
    return null;
  }
}

export function ChildApp() {
  const [screen, setScreen] = useState<Screen>({ at: "boot" });
  const [sitting, setSitting] = useState<Sitting | null>(null);
  const [problem, setProblem] = useState("");
  const where = useRef<Where | null>(null);
  const lastInput = useRef(Date.now());
  const startedAt = useRef(0);

  const queue: EventQueue | null = useMemo(
    () => (sitting ? createEventQueue((evs) => childApi.events(evs), { key: sitting.session, beacon: sendBeacon }) : null),
    [sitting],
  );

  const start = useCallback(async (pick: number | null, shared: boolean, names: string[], id = ulid()) => {
    const r = await childApi.sitting(id, pick, BUILD);
    const s: Sitting = { session: r.session, name: r.name, pick, shared, names };
    startedAt.current = performance.now();
    lastInput.current = Date.now();
    setSitting(s);
    saveSitting(s);
    setScreen({ at: "home" });
  }, []);

  const toPicker = useCallback(
    async (disown = false) => {
      try {
        const p = await childApi.picker();
        // A device enrolled for one child shows no picker: her name in the header is enough.
        if (!p.shared) await start(null, false, p.names);
        else setScreen({ at: "picker", picker: p, list: disown, disown });
      } catch {
        setScreen({ at: "enroll" });
      }
    },
    [start],
  );

  const boot = useCallback(async () => {
    setProblem("");
    void readLayout();
    let p: Picker;
    try {
      p = await childApi.picker();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setScreen({ at: "enroll" });
        return;
      }
      setProblem("Nepavyko prisijungti prie Ugio. Bandyk dar kartą.");
      return;
    }
    // A reload inside a sitting continues it (the same session id), unless a
    // shared device sat idle long enough for a sibling to have sat down.
    const prev = loadSitting();
    if (prev && (!p.shared || Date.now() - prev.at < IDLE_MS)) {
      try {
        await start(prev.pick, p.shared, p.names, prev.session);
        return;
      } catch {
        saveSitting(null);
      }
    }
    if (p.shared) {
      setScreen({ at: "picker", picker: p, list: false });
      return;
    }
    await start(null, false, p.names);
  }, [start]);

  useEffect(() => {
    void boot();
  }, [boot]);

  // A sitting that is gone server-side (idle on a shared device, a restart).
  const lost = useCallback(
    (e: unknown) => {
      if (e instanceof ApiError && (e.need === "picker" || e.need === "sitting")) {
        saveSitting(null);
        setSitting(null);
        void toPicker();
        return true;
      }
      if (e instanceof ApiError && e.status === 401) {
        saveSitting(null);
        setSitting(null);
        setScreen({ at: "enroll" });
        return true;
      }
      return false;
    },
    [toPicker],
  );

  const ctx: ChildCtx | null = useMemo(
    () =>
      sitting && queue
        ? {
            name: sitting.name,
            session: sitting.session,
            shared: sitting.shared,
            emit: makeEmit(queue),
            queue,
            setWhere: (w) => {
              where.current = w;
            },
            lost,
          }
        : null,
    [sitting, queue, lost],
  );

  // Visibility: session.blurred / .focused, the flush on hide, the beacon on pagehide.
  useEffect(() => {
    if (!ctx) return;
    let hiddenAt: number | null = null;
    const vis = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        ctx.emit("session.blurred", {});
        void ctx.queue.flush();
      } else if (hiddenAt !== null) {
        const ms = Date.now() - hiddenAt;
        hiddenAt = null;
        ctx.emit("session.focused", { hidden_ms: ms });
        if (ctx.shared && ms > IDLE_MS) {
          saveSitting(null);
          setSitting(null);
          void toPicker();
        }
      }
    };
    const hide = () => ctx.queue.beacon();
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("pagehide", hide);
    return () => {
      document.removeEventListener("visibilitychange", vis);
      window.removeEventListener("pagehide", hide);
    };
  }, [ctx, toPicker]);

  // S22: a shared device idle for ten minutes brings the picker back.
  useEffect(() => {
    if (!ctx?.shared) return;
    const touch = () => {
      lastInput.current = Date.now();
      const s = loadSitting();
      if (s) saveSitting({ session: s.session, name: s.name, pick: s.pick, shared: s.shared, names: s.names });
    };
    window.addEventListener("keydown", touch);
    window.addEventListener("pointerdown", touch);
    const t = setInterval(() => {
      if (Date.now() - lastInput.current > IDLE_MS) {
        saveSitting(null);
        setSitting(null);
        void toPicker();
      }
    }, 30_000);
    return () => {
      window.removeEventListener("keydown", touch);
      window.removeEventListener("pointerdown", touch);
      clearInterval(t);
    };
  }, [ctx, toPicker]);

  const leaveOpenItem = () => {
    const w = where.current;
    if (!ctx || !w) return;
    w.onLeave?.();
    if (w.open && w.item) ctx.emit("item.left", { item: w.item, state: w.state, rung: w.rung });
  };

  const baigti = async () => {
    if (!ctx) return;
    leaveOpenItem();
    await ctx.queue.flush();
    try {
      await childApi.end(Math.round(performance.now() - startedAt.current));
    } catch (e) {
      if (lost(e)) return;
    }
    saveSitting(null);
    setSitting(null);
    setScreen({ at: "ended" });
  };

  const notMe = async (pick: number) => {
    if (!sitting || !ctx) return;
    leaveOpenItem();
    await ctx.queue.flush();
    const r = await childApi.notMe({ disown_id: ulid(), id: ulid(), pick, at_item: where.current?.open ? where.current.item : undefined, build: BUILD });
    const s: Sitting = { session: r.session, name: r.name, pick, shared: true, names: sitting.names };
    setSitting(s);
    saveSitting(s);
    setScreen({ at: "home" });
  };

  if (screen.at === "boot") {
    return (
      <div className="center">
        {problem ? (
          <div className="card">
            <p className="lead">{problem}</p>
            <button type="button" className="btn" onClick={() => void boot()}>
              Bandyti dar kartą
            </button>
          </div>
        ) : null}
      </div>
    );
  }
  if (screen.at === "enroll") return <Enroll onDone={() => void boot()} />;
  if (screen.at === "picker") {
    return (
      <PickerScreen
        picker={screen.picker}
        list={screen.list}
        onList={() => setScreen({ ...screen, list: true })}
        onPick={(i) => void (screen.disown && sitting ? notMe(i) : start(i, true, screen.picker.names))}
      />
    );
  }
  if (screen.at === "ended") {
    return <Ended onAgain={() => void boot()} />;
  }
  if (!ctx || !sitting) return null;

  return (
    <Ctx.Provider value={ctx}>
      <div className="child">
        <Header
          name={sitting.name}
          shared={sitting.shared}
          onBaigti={() => void baigti()}
          onNotMe={() => void toPicker(true)}
          where={() => where.current}
        />
        <main className="child-main">
          <div className="column">
            {screen.at === "home" ? <Home name={sitting.name} onStart={() => setScreen({ at: "work" })} /> : <Work onFinished={() => void baigti()} />}
          </div>
        </main>
      </div>
    </Ctx.Provider>
  );
}

/* ============================================================================ the code */

function Enroll({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const c = code.replace(/\s+/g, "").toUpperCase();
    if (c.length < 4 || busy) return;
    setBusy(true);
    setMsg("");
    try {
      await childApi.enroll(c, navigator.userAgent.includes("Mac") ? "mac" : "kompiuteris");
      onDone();
    } catch (e) {
      setMsg(e instanceof ApiError && e.status === 429 ? "Per daug bandymų. Palauk kelias minutes." : "Toks kodas netinka. Patikrink ir įvesk dar kartą.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="center" data-screen="enroll">
      <div className="card">
        <h1>Labas!</h1>
        <p className="lead">Įvesk savo kodą. Jį reikia įvesti tik vieną kartą šiame kompiuteryje.</p>
        <input
          className="code-input"
          aria-label="Kodas"
          value={code}
          maxLength={8}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
          }}
        />
        {msg && <p className="note">{msg}</p>}
        <div className="actions">
          <button type="button" className="btn btn-solid" disabled={busy} onClick={() => void submit()} data-act="enroll">
            Toliau <span className="key-hint">Enter</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================ the picker (§5.6) */

function PickerScreen({ picker, list, onList, onPick }: { picker: Picker; list: boolean; onList: () => void; onPick: (i: number) => void }) {
  const last = picker.last ?? 0;
  const keys: Record<string, (e: KeyboardEvent) => void> = list
    ? Object.fromEntries(picker.names.map((_, i) => [`code:Digit${i + 1}`, () => onPick(i)]).concat(picker.names.map((_, i) => [`code:Numpad${i + 1}`, () => onPick(i)])))
    : { Enter: () => onPick(last), "code:KeyN": onList };
  useKeys(keys);
  return (
    <div className="center" data-screen="picker">
      <div className="card">
        {!list ? (
          <>
            <h1>Labas, {picker.names[last]}!</h1>
            <div className="actions" style={{ justifyContent: "flex-start" }}>
              <button type="button" className="btn btn-solid" onClick={() => onPick(last)} data-act="start">
                Pradėti <span className="key-hint">Enter</span>
              </button>
            </div>
            <p className="note">
              <button type="button" className="quiet-link" onClick={onList} data-act="not-me">
                ne aš
              </button>{" "}
              <span className="key-hint">· N</span>
            </p>
          </>
        ) : (
          <>
            <h1>Kas tu?</h1>
            <div className="picker-list">
              {picker.names.map((n, i) => (
                <button key={i} type="button" className="option" onClick={() => onPick(i)}>
                  <span className="num">{i + 1}</span>
                  {n}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================================ home and the slice (§5.3) */

function Home({ name, onStart }: { name: string; onStart: () => void }) {
  const [slice, setSlice] = useState<Slice | null>(null);
  const ctx = useChildOrNull();
  const shownAt = useRef(performance.now());
  useEffect(() => {
    childApi.slice().then(setSlice, () => setSlice(null));
  }, []);
  const go = () => {
    if (slice && ctx) ctx.emit("slice.viewed", { open_ms: Math.round(performance.now() - shownAt.current) });
    onStart();
  };
  useKeys({ Enter: go });
  return (
    <div data-screen="home">
      <h1 style={{ fontSize: 36 }}>Labas, {name}.</h1>
      <div className="actions" style={{ justifyContent: "flex-start" }}>
        <button type="button" className="btn btn-solid" onClick={go} data-act="start">
          Pradėti <span className="key-hint">Enter</span>
        </button>
      </div>
      {slice && <SliceView slice={slice} />}
    </div>
  );
}

function useChildOrNull() {
  return useContext(Ctx);
}

/** Her slice: the Topics of her plan and those she worked, one segment per
 *  Move, filled only by her own explicit pass. No numbers, no edges, no
 *  ordering by success. */
export function SliceView({ slice }: { slice: Slice }) {
  return (
    <section className="slice" aria-label="Tavo žemėlapis" data-slice>
      {slice.subjects.map((s) => (
        <div key={s.subject}>
          <h2>{s.title}</h2>
          {s.topics.map((t) => (
            <div key={t.topic} className="slice-row">
              <span>{t.title}</span>
              <span className="slice-cells">
                {t.moves.map((m) => (
                  <span key={m.move} className={`slice-cell ${m.filled ? "filled" : ""}`} aria-label={m.filled ? "išmokta" : "dar ne"} />
                ))}
              </span>
            </div>
          ))}
        </div>
      ))}
      <div className="slice-legend">
        <span>
          <span className="slice-cell filled" style={{ display: "inline-block", verticalAlign: "middle" }} /> išmokta
        </span>
        <span>
          <span className="slice-cell" style={{ display: "inline-block", verticalAlign: "middle" }} /> dar ne
        </span>
      </div>
    </section>
  );
}

function Ended({ onAgain }: { onAgain: () => void }) {
  useKeys({ Enter: onAgain });
  return (
    <div className="center" data-screen="ended">
      <div className="card">
        <h1>Iki kito karto.</h1>
        <div className="actions" style={{ justifyContent: "flex-start" }}>
          <button type="button" className="btn" onClick={onAgain}>
            Pradėti iš naujo <span className="key-hint">Enter</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================ the header */

type Req = { state: "idle" } | { state: "raised"; id: string } | { state: "taken" };

function Header({
  name,
  shared,
  onBaigti,
  onNotMe,
  where,
}: {
  name: string;
  shared: boolean;
  onBaigti: () => void;
  onNotMe: () => void;
  where: () => Where | null;
}) {
  const ctx = useChildOrNull();
  const [req, setReq] = useState<Req>({ state: "idle" });
  const [sending, setSending] = useState(false);

  // His pushes over her stream: taken, released, a request's state, a revoked device.
  useEffect(() => {
    let es: EventSource | null = null;
    let stop = false;
    const open = async () => {
      try {
        const st = await childApi.state();
        if (stop) return;
        // a reload (or a reset) inside a call knows it; outside one, a stale „taken" ends
        setReq((r) => (st.taken ? { state: "taken" } : r.state === "taken" ? { state: "idle" } : r));
        es = new EventSource(`/api/v1/child/stream?since=${encodeURIComponent(st.cursor)}`);
        es.addEventListener("taken", () => setReq({ state: "taken" }));
        es.addEventListener("released", () => setReq({ state: "idle" }));
        es.addEventListener("request", (m) => {
          const d = JSON.parse((m as MessageEvent).data) as { request?: { id: string; state: string } };
          if (d.request?.state === "raised") setReq((r) => (r.state === "taken" ? r : { state: "raised", id: d.request!.id }));
          else setReq((r) => (r.state === "raised" && r.id === d.request?.id ? { state: "idle" } : r));
        });
        es.addEventListener("revoked", () => {
          saveSitting(null);
          window.location.reload();
        });
        es.addEventListener("reset", () => {
          es?.close();
          if (!stop) setTimeout(() => void open(), 1000);
        });
      } catch {
        /* no stream: the request button still works, it just cannot hear him */
      }
    };
    void open();
    return () => {
      stop = true;
      es?.close();
    };
  }, []);

  const raise = async () => {
    if (!ctx || sending) return;
    setSending(true);
    const id = ulid();
    const w = where();
    try {
      await ctx.queue.flush();
      await childApi.raise({ id, where: w?.where ?? "idle", ...(w?.item ? { item: w.item } : {}), ...(w?.test ? { test: w.test } : {}) });
      setReq({ state: "raised", id });
    } catch (e) {
      ctx.lost(e);
    } finally {
      setSending(false);
    }
  };
  const withdraw = async () => {
    if (!ctx || req.state !== "raised" || sending) return;
    setSending(true);
    try {
      await ctx.queue.flush();
      await childApi.withdraw(ulid(), req.id);
      setReq({ state: "idle" });
    } catch (e) {
      ctx.lost(e);
    } finally {
      setSending(false);
    }
  };

  const taken = req.state === "taken";
  return (
    <header className="child-header">
      <div className="child-name" data-child-name>
        {name}
      </div>
      <div className="child-tools">
        <ChildVoiceIndicator taken={taken} />
        <div className="header-links">
          {req.state === "idle" && (
            <button type="button" className="btn btn-quiet request" onClick={() => void raise()} disabled={sending} data-act="request">
              ✋ Klausimas
            </button>
          )}
          {req.state === "raised" && (
            <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              <span className="btn btn-quiet request raised" aria-live="polite">
                ✋ Laukiu
              </span>
              <button type="button" className="quiet-link" onClick={() => void withdraw()} disabled={sending} data-act="withdraw">
                Atšaukti
              </button>
            </span>
          )}
          {taken && <span className="btn btn-quiet request raised">Kalbamės</span>}
          <button type="button" className="quiet-link" onClick={onBaigti} data-act="baigti">
            Baigti
          </button>
          {shared && (
            <button type="button" className="quiet-link" onClick={onNotMe} data-act="header-not-me">
              ne aš
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
